import { comCorpo, pedir, SemSessao } from '@/lib/pedido';

/**
 * A porta do módulo da plataforma para o backend (decisão 21, migração 0029;
 * a gestão das empresas, decisão 25, migração 0032).
 * As rotas são `/plataforma/*`, com o cookie próprio `ctf_plataforma`, que o
 * navegador manda sozinho.
 */

export interface EmpresaDaPlataforma {
  readonly empresaId: string;
  readonly nome: string;
  readonly criadoEm: string;
  readonly pessoas: number;
}

/** O detalhe de uma empresa para a gestão (0032, decisão 25): acesso, e não operação. */
export interface EmpresaGerida {
  readonly empresaId: string;
  readonly nome: string;
  readonly criadoEm: string;
  readonly ativa: boolean;
  readonly pessoasAtivas: number;
  readonly pessoas: number;
  readonly comAcesso: number;
  readonly convitesAbertos: number;
  readonly sessoesAbertas: number;
  readonly papeis: number;
}

export interface PessoaGerida {
  readonly usuarioId: string;
  readonly nome: string;
  readonly email: string | null;
  readonly ativo: boolean;
  readonly papeis: readonly { readonly id: string; readonly codigo: string; readonly nome: string }[];
  readonly temAcesso: boolean;
  readonly conviteAte: string | null;
  readonly sessoesAbertas: number;
  readonly criadoEm: string;
}

export interface PapelDaEmpresa {
  readonly papelId: string;
  readonly codigo: string;
  readonly nome: string;
  readonly pessoas: number;
}

export interface DetalheDaEmpresa {
  readonly empresa: EmpresaGerida;
  readonly pessoas: readonly PessoaGerida[];
  readonly papeis: readonly PapelDaEmpresa[];
}

export interface AdministradorDaPlataforma {
  readonly administradorId: string;
  readonly nome: string;
  readonly email: string;
  readonly ativo: boolean;
  readonly temSenha: boolean;
  readonly conviteAte: string | null;
  readonly criadoEm: string;
  readonly eu: boolean;
}

export interface AtoDaPlataforma {
  readonly ocorridoEm: string;
  readonly administrador: string;
  readonly acao: string;
  readonly empresa: string | null;
  readonly detalhe: Readonly<Record<string, unknown>>;
}

export interface ConviteDeAdministrador {
  readonly codigo: string;
  readonly expiraEm: string;
}

/*
 * O motor de IA, configurado pela plataforma (decisão do dono, 27/09/2026:
 * "os modelos de IA eu defino em plataforma"; migração 0042). A CHAVE não
 * existe em nenhum destes tipos: ela mora só em IA_CHAVE, no servidor, e a
 * tela sabe apenas se ela está lá.
 */

export type ProtocoloDaIa = 'gemini' | 'anthropic' | 'openai';

/** O que se testa e o que se salva. No arquivo, `null` é "o principal"; no vetor, "sem índice por sentido". */
export interface ParametrosDaIa {
  readonly protocolo: ProtocoloDaIa;
  readonly endereco: string;
  readonly modelo: string;
  readonly modeloArquivo: string | null;
  readonly modeloVetor: string | null;
}

export interface ConfiguracaoDaIaSalva extends ParametrosDaIa {
  readonly atualizadoEm: string;
  readonly atualizadoPor: { readonly nome: string; readonly email: string } | null;
}

export type OrigemDaIa = 'configuracoes' | 'ambiente' | 'nenhuma';

/** A IA que ESTE processo da API usa agora, com o arquivo e o vetor já resolvidos. */
export interface IaEmUso {
  readonly origem: OrigemDaIa;
  readonly protocolo: ProtocoloDaIa | null;
  readonly endereco: string | null;
  readonly modelo: string | null;
  readonly modeloArquivo: string | null;
  readonly modeloVetor: string | null;
  readonly ligada: boolean;
  readonly leArquivos: boolean;
  readonly temVetores: boolean;
  /** Só nomes de variável, como `IA_CHAVE`. */
  readonly faltando: readonly string[];
}

export interface ConfiguracaoDaIaDaPlataforma {
  readonly salva: ConfiguracaoDaIaSalva | null;
  readonly emUso: IaEmUso;
  readonly chaveNoAmbiente: boolean;
}

export interface ModeloDoProvedor {
  readonly id: string;
  readonly nome: string;
  /** `text`, `image`, `file`, `audio`, `video`, como o provedor informa; vazio quando não informa. */
  readonly entradas: readonly string[];
  readonly contexto: number | null;
  /** US$ por 1 milhão de tokens. */
  readonly precoEntradaPorMilhao: number | null;
  readonly precoSaidaPorMilhao: number | null;
  readonly respostaEmJson: boolean | null;
  readonly expiraEm: string | null;
}

export interface ListaDeModelos {
  readonly listaDisponivel: boolean;
  readonly comChave: boolean;
  readonly modelos: readonly ModeloDoProvedor[];
  readonly modelosDeVetor: readonly ModeloDoProvedor[];
  readonly consultadoEm: string;
}

export interface EtapaDoTeste {
  readonly ok: boolean;
  readonly modelo: string;
  readonly ms: number;
  readonly detalhe: string | null;
  readonly comoResolver: string | null;
}

export interface ResultadoDoTesteDaIa {
  readonly resposta: EtapaDoTeste;
  /** `null` quando o protocolo não lê arquivo. */
  readonly leitura: EtapaDoTeste | null;
  /** `null` sem modelo de vetores. */
  readonly vetores: EtapaDoTeste | null;
  readonly testadoEm: string;
}

const CONFIGURACAO_DA_IA = '/plataforma/configuracoes/ia';

/* O corpo vai campo a campo: a rota recusa campo a mais (`.strict()`), e um
   espalhamento levaria junto o que o objeto de quem chama tiver de sobra. */
const corpoDosParametros = (p: ParametrosDaIa) => ({
  protocolo: p.protocolo,
  endereco: p.endereco,
  modelo: p.modelo,
  modeloArquivo: p.modeloArquivo,
  modeloVetor: p.modeloVetor,
});

const colocar = <T,>(caminho: string, corpo: unknown) => pedir<T>(caminho, { method: 'PUT', body: JSON.stringify(corpo) });

const pessoa = (empresaId: string, usuarioId: string) =>
  `/plataforma/empresas/${encodeURIComponent(empresaId)}/pessoas/${encodeURIComponent(usuarioId)}`;

export const api = {
  /** Quem está na sessão de plataforma, ou `null` sem sessão. */
  /* `conta`: a empresa da conta de suporte vinculada (0134), para a troca de ambiente. */
  async eu(): Promise<{ readonly nome: string; readonly email: string; readonly conta?: { readonly empresa: string } } | null> {
    try {
      return await pedir<{ nome: string; email: string; conta?: { empresa: string } }>('/plataforma/sessao/atual');
    } catch (erro) {
      if (erro instanceof SemSessao) return null;
      throw erro;
    }
  },
  entrar: (email: string, senha: string) => comCorpo<{ entrou: true }>('POST', '/plataforma/sessao', { email, senha }),
  sair: () => comCorpo<{ saiu: true }>('DELETE', '/plataforma/sessao'),
  empresas: async () => (await pedir<{ empresas: readonly EmpresaDaPlataforma[] }>('/plataforma/empresas')).empresas,
  criarEmpresa: (nome: string, administrador: { readonly nome: string; readonly email: string }) =>
    comCorpo<ConviteDeAdministrador & { empresaId: string }>('POST', '/plataforma/empresas', { nome, administrador }),
  reemitirConvite: (empresaId: string) =>
    comCorpo<ConviteDeAdministrador & { email: string }>('POST', `/plataforma/empresas/${encodeURIComponent(empresaId)}/convite`),
  empresa: (empresaId: string) => pedir<DetalheDaEmpresa>(`/plataforma/empresas/${encodeURIComponent(empresaId)}`),
  renomearEmpresa: (empresaId: string, nome: string, motivo: string) =>
    comCorpo<{ renomeada: true }>('PATCH', `/plataforma/empresas/${encodeURIComponent(empresaId)}`, { nome, motivo }),
  cadastrarPessoa: (empresaId: string, pessoa: { readonly nome: string; readonly email: string; readonly papelId: string | null }) =>
    comCorpo<ConviteDeAdministrador & { usuarioId: string }>('POST', `/plataforma/empresas/${encodeURIComponent(empresaId)}/pessoas`, pessoa),
  situacaoDaPessoa: (empresaId: string, usuarioId: string, ativo: boolean, motivo: string) =>
    colocar<{ ativo: boolean; sessoesEncerradas: number }>(`${pessoa(empresaId, usuarioId)}/situacao`, { ativo, motivo }),
  papelDaPessoa: (empresaId: string, usuarioId: string, papelId: string, conceder: boolean, motivo: string) =>
    colocar<{ concedido: boolean }>(`${pessoa(empresaId, usuarioId)}/papeis/${encodeURIComponent(papelId)}`, { conceder, motivo }),
  conviteDaPessoa: (empresaId: string, usuarioId: string) =>
    comCorpo<ConviteDeAdministrador & { email: string }>('POST', `${pessoa(empresaId, usuarioId)}/convite`),
  encerrarSessoes: (empresaId: string, usuarioId: string, motivo: string) =>
    comCorpo<{ encerradas: number }>('POST', `${pessoa(empresaId, usuarioId)}/encerramento-de-sessoes`, { motivo }),
  empresasDesativadas: async () =>
    (await pedir<{ empresas: readonly string[] }>('/plataforma/empresas-desativadas')).empresas,
  situacaoDaEmpresa: (empresaId: string, ativa: boolean, motivo: string) =>
    colocar<{ ativa: boolean; sessoesEncerradas: number; convitesAnulados: number }>(
      `/plataforma/empresas/${encodeURIComponent(empresaId)}/situacao`,
      { ativa, motivo },
    ),
  /** Abre a sessão de suporte (decisão 26): o cookie de empresa vem na resposta. */
  abrirSuporte: (empresaId: string, motivo: string) =>
    comCorpo<{ expiraEm: string }>('POST', `/plataforma/empresas/${encodeURIComponent(empresaId)}/suporte`, { motivo }),
  administradores: async () =>
    (await pedir<{ administradores: readonly AdministradorDaPlataforma[] }>('/plataforma/administradores')).administradores,
  convidarAdministrador: (nome: string, email: string) =>
    comCorpo<ConviteDeAdministrador>('POST', '/plataforma/administradores', { nome, email }),
  situacaoDoAdministrador: (administradorId: string, ativo: boolean) =>
    colocar<{ ativo: boolean }>(`/plataforma/administradores/${encodeURIComponent(administradorId)}/situacao`, { ativo }),
  trilha: async (limite = 200) =>
    (await pedir<{ trilha: readonly AtoDaPlataforma[] }>(`/plataforma/trilha?limite=${String(limite)}`)).trilha,
  aceitarConvite: (codigo: string, senha: string) =>
    comCorpo<{ aceito: true; email: string }>('POST', '/plataforma/convite/aceite', { codigo, senha }),
  configuracaoDaIa: () => pedir<ConfiguracaoDaIaDaPlataforma>(CONFIGURACAO_DA_IA),
  /** O ambiente de homologação (0080): liga o modo histórias para todas as empresas deste ambiente. */
  ambiente: () => pedir<{ homologacao: boolean }>('/plataforma/configuracoes/ambiente'),
  definirAmbiente: (homologacao: boolean, motivo: string) =>
    colocar<{ homologacao: boolean }>('/plataforma/configuracoes/ambiente', { homologacao, motivo }),
  salvarConfiguracaoDaIa: (p: ParametrosDaIa, motivo: string) =>
    colocar<ConfiguracaoDaIaDaPlataforma>(CONFIGURACAO_DA_IA, { ...corpoDosParametros(p), motivo }),
  removerConfiguracaoDaIa: (motivo: string) =>
    comCorpo<ConfiguracaoDaIaDaPlataforma>('POST', `${CONFIGURACAO_DA_IA}/remocao`, { motivo }),
  /** A API pede a lista ao provedor: o navegador nunca fala com ele. */
  modelosDaIa: (protocolo: ProtocoloDaIa, endereco: string) =>
    pedir<ListaDeModelos>(
      `${CONFIGURACAO_DA_IA}/modelos?protocolo=${encodeURIComponent(protocolo)}&endereco=${encodeURIComponent(endereco)}`,
    ),
  testarIa: (p: ParametrosDaIa) => comCorpo<ResultadoDoTesteDaIa>('POST', `${CONFIGURACAO_DA_IA}/teste`, corpoDosParametros(p)),
};
